import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject, ErrorObject } from '@inmoment/the-kitchen';

import { Scale } from 'app/components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import {
  QueryMatching,
  TestDocumentGeneratorConfig,
  TestDocumentGeneratorService,
} from '../../services/test-document-generator.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-test-document-generator-dialog',
  templateUrl: './test-document-generator-dialog.component.html',
  styleUrls: ['./test-document-generator-dialog.component.scss'],
  standalone: false,
})
export class TestDocumentGeneratorDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  countScale: Scale;

  readonly responseTypeOptions: DropdownObject[] = [
    { title: 'Survey response', id: 'survey response' },
    { title: 'Online review', id: 'online review' },
    { title: 'Social media post', id: 'social media post' },
  ];
  readonly styleOptions: DropdownObject[] = [
    { title: 'Casual', id: 'casual' },
    { title: 'Formal', id: 'formal' },
    { title: 'Mobile', id: 'mobile' },
  ];
  readonly queryMatchRadios = [
    {
      label: "Don't care",
      value: QueryMatching.MIXED,
    },
    {
      label: 'Should hit',
      value: QueryMatching.HIT,
    },
    { label: "Shouldn't hit", value: QueryMatching.NO_HIT },
  ];

  errorObjects: { [name: string]: ErrorObject } = {};
  formGroup: FormGroup;
  count = 1;

  isEvaluationInProgress = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      maxCount: number;
      query: string;
      initialPhrase?: string;
    },
    private matDialog: MatDialogRef<TestDocumentGeneratorDialogComponent>,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private alertService: AlertService,
    private testDocumentGeneratorService: TestDocumentGeneratorService
  ) {}

  ngOnInit(): void {
    this.createFormGroup();
    this.countScale = {
      min: 1,
      max: this.data.maxCount,
      step: 1,
    };
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private createFormGroup(): void {
    this.formGroup = this.fb.group({
      typeOfResponse: [this.responseTypeOptions[0].id, Validators.required],
      about: ['', Validators.required],
      inStyleOf: [this.styleOptions[0].id, Validators.required],
      sentiment: this.fb.group({
        positive: [false],
        negative: [false],
        neutral: [false],
      }),
      focus: [''],
      queryMatch: [QueryMatching.MIXED, Validators.required],
    });

    this.subscribeToFormControlStatusChanges('about', 'About');
    this.subscribeToFormControlStatusChanges('focus', 'Focus on');
  }

  private subscribeToFormControlStatusChanges(formControlName: string, fieldName: string): void {
    const ctrl = this.formGroup.controls[formControlName];
    ctrl.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe((status) => {
      this.errorObjects[formControlName] = ErrorObjectUtils.create();
      this.validateInput(status, fieldName, ctrl.errors, this.errorObjects[formControlName]);
      this.cdRef.markForCheck();
    });
  }

  private validateInput(
    formStatus: string,
    fieldName: string,
    errors: ValidationErrors,
    input: ErrorObject
  ) {
    if (formStatus === 'INVALID' && errors) {
      switch (Object.keys(errors)[0]) {
        case 'required':
          ErrorObjectUtils.setError(input, `${fieldName} is required`);
          break;
      }
    } else {
      ErrorObjectUtils.setNoError(input);
    }
  }

  private getGeneratorConfig(): TestDocumentGeneratorConfig {
    const formValues = this.formGroup.value;

    const config: TestDocumentGeneratorConfig = {
      count: this.count,
      typeOfResponse: formValues.typeOfResponse,
      about: formValues.about,
      inStyleOf: formValues.inStyleOf,
      sentiment: this.getSelectedSentimentAsText(),
      minSentences: 1,
      maxSentences: 10,
      focus: formValues.focus,
      queryMatch: formValues.queryMatch,
      query: this.data.query,
    };

    return config;
  }

  private getSelectedSentimentAsText(): string {
    const selected = [];
    const formValues = this.formGroup.value;

    if (formValues.sentiment.positive) {
      selected.push('positive');
    }
    if (formValues.sentiment.negative) {
      selected.push('negative');
    }
    if (formValues.sentiment.neutral) {
      selected.push('neutral');
    }

    return selected.join(', ');
  }

  onGenerate(): void {
    Object.values(this.formGroup.controls).forEach((ctrl) => ctrl.updateValueAndValidity());

    if (this.formGroup.valid) {
      this.isEvaluationInProgress = true;

      this.testDocumentGeneratorService
        .generateTestDocuments(this.getGeneratorConfig())
        .then(this.onGenerationSuccess.bind(this), this.onGenerationError.bind(this));
    }
  }

  private onGenerationSuccess(result: any): void {
    this.matDialog.close(result);
  }

  private onGenerationError(error: HttpErrorResponse | Error): void {
    this.alertService.showErrorToast('Unable to generate test documents', error);
    this.isEvaluationInProgress = false;
    this.cdRef.markForCheck();
  }

  onCancel(): void {
    this.matDialog.close();
  }
}
